import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listAdmin, deleteAdmin, listDownloads } from "@/lib/adminApi";
import { Plus, Edit2, Trash2, Eye, EyeOff, Loader2, Download } from "lucide-react";

const ENTITY_META = {
  publications: { title: "الإصدارات المعرفية", singular: "إصدار", publicPath: "/publications" },
  news: { title: "الأخبار", singular: "خبر", publicPath: "/news" },
  projects: { title: "المشاريع", singular: "مشروع", publicPath: "/impact" },
};

export default function AdminList({ entity }) {
  const meta = ENTITY_META[entity];
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState({});
  const [dlStats, setDlStats] = useState({});

  const load = () =>
    listAdmin(entity)
      .then(setItems)
      .catch(() => setItems([]));

  useEffect(() => {
    load();
    if (entity === "publications") {
      listDownloads()
        .then((d) => setDlStats(d.stats || {}))
        .catch(() => {});
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!window.confirm("هل تريد حذف هذا العنصر نهائيًا؟")) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await deleteAdmin(entity, id);
      await load();
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  if (items === null) {
    return (
      <div className="grid place-items-center py-20">
        <Loader2 className="animate-spin text-blue" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl">{meta.title}</h1>
          <p className="text-blue/70 text-sm mt-1">
            {items.length} عنصر — {items.filter((i) => i.status === "published").length} منشور
          </p>
        </div>
        <button
          onClick={() => navigate(`/admin/${entity}/new`)}
          data-testid={`add-${entity}`}
          className="inline-flex items-center gap-2 bg-coral text-white px-4 py-2.5 text-sm font-semibold"
        >
          <Plus size={16} /> إضافة {meta.singular} جديد
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-navy/10 p-8 text-center text-blue/70">
          لا يوجد {meta.singular} بعد. اضغط زر «إضافة» أعلاه للبدء.
        </div>
      ) : (
        <div className="bg-white border border-navy/10 overflow-x-auto">
          <table className="w-full text-right text-sm">
            <thead className="bg-paper/50 text-blue/70">
              <tr>
                <th className="p-3 font-medium">العنوان</th>
                <th className="p-3 font-medium w-24">الحالة</th>
                {entity === "publications" && <th className="p-3 font-medium w-28">النوع</th>}
                <th className="p-3 font-medium w-32">تاريخ النشر</th>
                {entity === "publications" && <th className="p-3 font-medium w-24">التحميلات</th>}
                <th className="p-3 font-medium w-32">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-navy/5 hover:bg-paper/30">
                  <td className="p-3">
                    <div className="font-medium text-navy">{it.title}</div>
                    <div className="text-xs text-blue/60 mt-0.5" dir="ltr">/{it.slug}</div>
                  </td>
                  <td className="p-3">
                    {it.status === "published" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><Eye size={12} />منشور</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-blue/60"><EyeOff size={12} />مسودة</span>
                    )}
                  </td>
                  {entity === "publications" && (
                    <td className="p-3 text-xs text-blue/70">{typeLabel(it.type)}</td>
                  )}
                  <td className="p-3 text-xs text-blue/70">
                    {it.published_at ? new Date(it.published_at).toLocaleDateString("ar-SA") : "—"}
                  </td>
                  {entity === "publications" && (
                    <td className="p-3" data-testid={`dl-count-${it.slug}`}>
                      <span className="inline-flex items-center gap-1 text-navy font-semibold">
                        <Download size={13} className="text-blue/60" />
                        {dlStats[it.slug]?.count ?? 0}
                      </span>
                    </td>
                  )}
                  <td className="p-3">
                    <div className="flex gap-2">
                      <Link
                        to={`/admin/${entity}/edit/${it.id}`}
                        className="inline-flex items-center gap-1 text-xs text-navy hover:text-coral"
                        data-testid={`edit-${it.id}`}
                      >
                        <Edit2 size={13} /> تعديل
                      </Link>
                      <button
                        onClick={() => remove(it.id)}
                        disabled={!!busy[it.id]}
                        className="inline-flex items-center gap-1 text-xs text-coral hover:text-red-700 disabled:opacity-40"
                        data-testid={`del-${it.id}`}
                      >
                        <Trash2 size={13} /> حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function typeLabel(t) {
  return { article: "مقالة", release: "إصدار", "case-study": "دراسة حالة", research: "بحث" }[t] || "—";
}
